import React, { useState } from 'react';
import { BygrProfileDetails, InvitationStatus, ProfileInvite } from '@/types';
import { createDataTable } from '@/components/DataTable';
import { Toast, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import InviteProfileMemberModal from '@/components/Profiles/InviteProfileMemberModal';
import { useAsyncState } from '@/utils/hooks';
import { getSystemInfo } from '@/services/system';
import { cancelProfileInvitation, resendProfileInvitation } from '@/services/profile-invitations';
import { Button, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}
const InviteDataTable = createDataTable<ProfileInvite>();

const ProfileInvites: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [systemInfo] = useAsyncState(() => getSystemInfo(), [], null);

    const [isLoading, setIsLoading] = useState(false);
    const [inviteTableState, setInviteTableState] = useState(InviteDataTable.getInitialState('id'));

    const [inviteModalIsOpen, setInviteModalIsOpen] = useState(false);

    const resendInvitation = async (id: number, reload: boolean, successMessage: string) => {
        setIsLoading(true);
        await resendProfileInvitation(id);
        if (reload) {
            reloadProfile();
        }
        setIsLoading(false);
        Toast.success(successMessage);
    };

    if (!profile) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <InviteDataTable
                onStateChange={setInviteTableState}
                state={inviteTableState}
                data={profile.invitations}
                totalItemCount={profile.invitations.length}
                toolbarActions={
                    <Button size="sm" onClick={() => setInviteModalIsOpen(true)} variant="primary">
                        Invite user
                    </Button>
                }
                className="table style-3">
                <InviteDataTable.Column header="Invite Id" field="id" renderer={({ value }) => value} />
                <InviteDataTable.Column header="Email" field="email" renderer={({ value }) => value} />
                <InviteDataTable.Column header="First Name" field="firstName" renderer={({ value }) => value} />
                <InviteDataTable.Column header="Last Name" field="lastName" renderer={({ value }) => value} />
                <InviteDataTable.Column
                    header="Is Admin"
                    field="isAdministrator"
                    renderer={({ value }) => (value ? 'Yes' : 'No')}
                />
                <InviteDataTable.Column header="Status" field="status" renderer={({ value }) => value} />
                <InviteDataTable.Column
                    header="Copy invitation link"
                    field="token"
                    renderer={({ value }) => (
                        <Button
                            // disabled={row.status !== InvitationStatus.Sent}
                            loading={isLoading}
                            onClick={async () => {
                                const inviteUrl = `${systemInfo?.frontendUrl}/cm/invitation?token=${value}`;

                                navigator.clipboard.writeText(inviteUrl);
                                Toast.success('Copied invitation link to clipboard');
                            }}
                            variant="tertiary" size="xs">
                            Copy invitation link
                        </Button>
                    )}
                />
                <InviteDataTable.Column
                    header="Resend invitation"
                    field="status"
                    renderer={({ value, row }) => {
                        if (value === InvitationStatus.Registered) {
                            return null;
                        }

                        if (row.status === InvitationStatus.Canceled) {
                            return (
                                <ConfirmButton
                                    size="xs"
                                    dialogTitle="Reactivate and resend?"
                                    dialogBody="This invitation was cancelled. Resending will reactivate it (status back to Sent) and send a new invitation email."
                                    loading={isLoading}
                                    onConfirm={() => resendInvitation(row.id, true, 'Invitation reactivated and resent')}
                                    variant="tertiary">
                                    Resend invitation
                                </ConfirmButton>
                            );
                        }

                        return (
                            <Button
                                size="xs"
                                disabled={row.status !== InvitationStatus.Sent}
                                loading={isLoading}
                                onClick={() => resendInvitation(row.id, false, 'Resent invitation')}
                                variant="tertiary">
                                Resend invitation
                            </Button>
                        );
                    }}
                />
                <InviteDataTable.Column
                    header="Cancel invitation"
                    field="status"
                    renderer={({ value, row }) =>
                        value !== InvitationStatus.Registered && (
                            <ConfirmButton
                                size="xs"
                                disabled={row.status === InvitationStatus.Canceled}
                                dialogTitle="Are you sure?"
                                dialogBody="This will cancel the invitation. You can reactivate it later by resending it."
                                loading={isLoading}
                                onConfirm={async () => {
                                    setIsLoading(true);
                                    await cancelProfileInvitation(row.id);
                                    reloadProfile();
                                    setIsLoading(false);
                                    Toast.success('Invitation canceled');
                                }}
                                variant="danger"
                                confirmButton={{ variant: 'danger' }}>
                                Cancel invitation
                            </ConfirmButton>
                        )
                    }
                />
            </InviteDataTable>

            <InviteProfileMemberModal
                isOpen={inviteModalIsOpen}
                profile={profile}
                toggle={() => setInviteModalIsOpen(false)}
                onSave={() => {
                    reloadProfile();
                }}
            />
        </>
    );
};

export default ProfileInvites;
